/* Wholesale Picklist – supports sticky bar OUTSIDE the custom element + live "In cart" counts + PO saved to cart attributes */

if (!customElements.get('wholesale-picklist')) {
  class WholesalePicklist extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.openDrawer = this.dataset.openDrawer === 'true';

      // Error message text for quantity selection
      this.qtyErrorText = 'Add a quantity for at least one item.';

      // Elements INSIDE the custom element
      this.errorEl = null; // uses floating alert in sticky bar (data-wholesale-error-floating)
      this.statusEl = this.querySelector('[data-wholesale-status]');

      // Elements that may be OUTSIDE the custom element (sticky bar)
      this.addBtn = null;
      this.barMsgEl = null;
      this.placeholderHtml = '';

      // PO input (you added this into the floating bar)
      this.poInput = null;
      this.onPoChange = null;
      this.onPoInput = null;
      this._lastSavedPo = null;

      this.cartDrawer = document.querySelector('cart-drawer');

      this.onAdd = (evt) => {
        evt.preventDefault();
        this.addSelectedToCart();
      };
    }

    connectedCallback() {
      // Bind now (may not find sticky bar yet), and re-try after render
      this.bindExternalControls();
      requestAnimationFrame(() => this.bindExternalControls());
      setTimeout(() => this.bindExternalControls(), 200);

      // Clear the quantity-required error as soon as a qty is entered
      this.bindQtyWatcher();

      // Sync message + in-cart counts on load (and again after sticky bar exists)
      this.syncFromCart();
      setTimeout(() => this.syncFromCart(), 250);

      // Initialise disclosure galleries only after the panel has fully opened
      this.detailsEls = this.querySelectorAll('.wholesale-picklist__details');
      this.detailsPanels = this.querySelectorAll('.wholesale-picklist__details > .disclosure__panel');
      this._galleryInitTimers = new WeakMap();

      this.syncDisclosureGallery = (gallery) => {
        if (!gallery || typeof gallery.setVisibleItems !== 'function') return;

        gallery.setVisibleItems();
        if (!gallery.visibleItems || gallery.visibleItems.length === 0) return;

        const activeItem = gallery.querySelector('.media-viewer__item.is-active:not([hidden])') || gallery.visibleItems[0];

        if (!gallery.initialised && typeof gallery.initGallery === 'function') {
          gallery.initGallery();
        }

        gallery.setVisibleItems();
        if (!gallery.visibleItems || gallery.visibleItems.length === 0) return;

        const visibleActiveItem = gallery.visibleItems.includes(activeItem) ? activeItem : gallery.visibleItems[0];

        if (gallery.visibleItems.length > 1) {
          gallery.viewerItemOffset = gallery.visibleItems[1].offsetLeft - gallery.visibleItems[0].offsetLeft;
        }

        gallery.currentItem = null;
        gallery.viewer.scrollLeft = visibleActiveItem.offsetLeft;
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(visibleActiveItem, false);
        }
      };

      this.onDetailsToggle = (evt) => {
        const disclosure = evt.currentTarget;
        if (!disclosure.open) return;

        const panel = disclosure.querySelector(':scope > .disclosure__panel');
        if (!panel) return;

        const timer = window.setTimeout(() => {
          panel.querySelectorAll('media-gallery').forEach((gallery) => this.syncDisclosureGallery(gallery));
          this._galleryInitTimers.delete(panel);
        }, 340);

        const existingTimer = this._galleryInitTimers.get(panel);
        if (existingTimer) {
          window.clearTimeout(existingTimer);
        }
        this._galleryInitTimers.set(panel, timer);
      };

      this.onDetailsPanelTransitionEnd = (evt) => {
        if (evt.propertyName && evt.propertyName !== 'height') return;

        const panel = evt.currentTarget;
        const disclosure = panel.closest('.wholesale-picklist__details');
        if (!disclosure || !disclosure.open) return;

        const existingTimer = this._galleryInitTimers.get(panel);
        if (existingTimer) {
          window.clearTimeout(existingTimer);
          this._galleryInitTimers.delete(panel);
        }

        panel.querySelectorAll('media-gallery').forEach((gallery) => this.syncDisclosureGallery(gallery));
      };

      this.detailsEls.forEach((el) => {
        el.addEventListener('toggle', this.onDetailsToggle);
      });

      this.detailsPanels.forEach((panel) => {
        panel.addEventListener('transitionend', this.onDetailsPanelTransitionEnd);
      });
    }

    disconnectedCallback() {
      if (this.addBtn) this.addBtn.removeEventListener('click', this.onAdd);

      if (this.poInput && this.onPoChange) {
        this.poInput.removeEventListener('change', this.onPoChange);
        this.poInput.removeEventListener('blur', this.onPoChange);
      }

      if (this.poInput && this.onPoInput) {
        this.poInput.removeEventListener('input', this.onPoInput);
      }

      this.unbindQtyWatcher();

      if (this.detailsEls && this.onDetailsToggle) {
        this.detailsEls.forEach((el) => {
          el.removeEventListener('toggle', this.onDetailsToggle);
        });
      }

      if (this.detailsPanels && this.onDetailsPanelTransitionEnd) {
        this.detailsPanels.forEach((panel) => {
          panel.removeEventListener('transitionend', this.onDetailsPanelTransitionEnd);

          if (this._galleryInitTimers) {
            const timer = this._galleryInitTimers.get(panel);
            if (timer) {
              window.clearTimeout(timer);
              this._galleryInitTimers.delete(panel);
            }
          }
        });
      }
    }

    // -----------------------
    // Helpers to find elements
    // -----------------------
    getStickyBarRoot() {
      // Your liquid uses id="wholesale-info-bar-{{ section.id }}"
      return document.getElementById(`wholesale-info-bar-${this.sectionId}`);
    }

    findAddButton() {
      return (
        this.querySelector('[data-wholesale-add]') ||
        this.getStickyBarRoot()?.querySelector('[data-wholesale-add]') ||
        null
      );
    }

    findBarMessageEl() {
      return (
        this.querySelector('[data-wholesale-bar-message]') ||
        this.getStickyBarRoot()?.querySelector('[data-wholesale-bar-message]') ||
        null
      );
    }

    findPoInput() {
      // PO input is in the floating bar actions: data-wholesale-po
      return (
        this.querySelector('[data-wholesale-po]') ||
        this.getStickyBarRoot()?.querySelector('[data-wholesale-po]') ||
        null
      );
    }

    findErrorEl() {
      return (
        this.querySelector('[data-wholesale-error-floating]') ||
        this.getStickyBarRoot()?.querySelector('[data-wholesale-error-floating]') ||
        null
      );
    }

    getCartUpdateUrl() {
      // Alchemy provides theme.routes.cartUpdate; fall back to Shopify AJAX endpoint
      return (window.theme && theme.routes && theme.routes.cartUpdate) ? theme.routes.cartUpdate : '/cart/update.js';
    }

    bindExternalControls() {
      // Add button may live outside the web component now
      const btn = this.findAddButton();
      if (btn && btn !== this.addBtn) {
        if (this.addBtn) this.addBtn.removeEventListener('click', this.onAdd);
        this.addBtn = btn;
        this.addBtn.addEventListener('click', this.onAdd);
      }

      // Bar message may live outside too
      const msg = this.findBarMessageEl();
      if (msg && msg !== this.barMsgEl) {
        this.barMsgEl = msg;
        if (!this.placeholderHtml) this.placeholderHtml = this.barMsgEl.innerHTML;
      }

      // Floating error alert may live outside too
      const err = this.findErrorEl();
      if (err && err !== this.errorEl) {
        this.errorEl = err;
      }
      // PO input may live outside too
      const po = this.findPoInput();
      if (po && po !== this.poInput) {
        // unbind old if it existed
        if (this.poInput && this.onPoChange) {
          this.poInput.removeEventListener('change', this.onPoChange);
          this.poInput.removeEventListener('blur', this.onPoChange);
        }

        if (this.poInput && this.onPoInput) {
          this.poInput.removeEventListener('input', this.onPoInput);
        }

        this.poInput = po;
        this.onPoChange = () => { this.savePoNumber(); };
        this.onPoInput = () => {
          this.poInput.removeAttribute('aria-invalid');
          if (this.errorEl && !this.errorEl.hidden && this.errorEl.textContent === 'PO number is required.') {
            this.setError(null);
          }
        };

        this.poInput.addEventListener('change', this.onPoChange);
        this.poInput.addEventListener('blur', this.onPoChange);
        this.poInput.addEventListener('input', this.onPoInput);
      }

    }

    
    // -----------------------
    // Quantity input watcher (clears qty error once any qty > 0 is entered)
    // -----------------------
    bindQtyWatcher() {
      if (this._qtyWatcherBound) return;
      this._qtyWatcherBound = true;

      this.onQtyWatch = (e) => {
        const t = e.target;
        if (!t || !t.matches || !t.matches('input[data-variant-id]')) return;

        // Only clear the "qty required" error (do not clear PO required, etc.)
        if (!this.errorEl || this.errorEl.hidden) return;
        const msg = (this.errorEl.textContent || '').trim();
        if (msg !== this.qtyErrorText) return;

        const items = this.getSelectedItems();
        if (items.length > 0) this.setError(null);
      };

      this.addEventListener('input', this.onQtyWatch);
      this.addEventListener('change', this.onQtyWatch);
    }

    unbindQtyWatcher() {
      if (!this._qtyWatcherBound) return;
      this._qtyWatcherBound = false;

      if (this.onQtyWatch) {
        this.removeEventListener('input', this.onQtyWatch);
        this.removeEventListener('change', this.onQtyWatch);
      }
      this.onQtyWatch = null;
    }

// -----------------------
    // UI helpers
    // -----------------------
    setError(msg) {
      // error element is in the sticky bar (outside the custom element)
      if (!this.errorEl) {
        // try to locate it lazily
        this.errorEl = this.findErrorEl();
      }
      if (!this.errorEl) return;
      this.errorEl.hidden = !msg;
      this.errorEl.textContent = msg || '';
    }

    setStatus(msg) {
      if (!this.statusEl) return;
      this.statusEl.hidden = !msg;
      this.statusEl.textContent = msg || '';

      if (msg) {
        clearTimeout(this._statusTimer);
        this._statusTimer = setTimeout(() => {
          if (this.statusEl) this.statusEl.hidden = true;
        }, 4000);
      }
    }

    resetQuantities() {
      // Inputs are still inside the custom element
      const inputs = this.querySelectorAll('input[data-variant-id]');
      inputs.forEach((input) => {
        if (input.disabled) return;
        input.value = 0;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    getSelectedItems() {
      const inputs = this.querySelectorAll('input[data-variant-id]');
      const items = [];

      inputs.forEach((input) => {
        if (input.disabled) return;
        const qty = parseInt(input.value, 10);
        if (!qty || qty <= 0) return;

        const id = input.dataset.variantId;
        if (!id) return;

        items.push({ id, quantity: qty });
      });

      return items;
    }

    // -----------------------
    // PO -> cart attributes
    // -----------------------
    isPoRequired() {
      if (!this.poInput) return false;
      const v = this.poInput.dataset ? this.poInput.dataset.poRequired : undefined;
      if (typeof v !== 'undefined') return v === 'true';
      return this.poInput.hasAttribute('required');
    }

    validatePoRequired() {
      if (!this.poInput) return true;

      // Optional for Wholesale (not approved) customer tag
      if (!this.isPoRequired()) {
        this.poInput.removeAttribute('aria-invalid');
        if (this.errorEl && !this.errorEl.hidden && (this.errorEl.textContent || '').trim() === 'PO number is required.') {
          this.setError(null);
        }
        return true;
      }
      const po = (this.poInput.value || '').trim();
      const ok = po.length > 0;

      this.poInput.toggleAttribute('aria-invalid', !ok);

      if (!ok) {
        this.setError('PO number is required.');
        this.poInput.focus();
      } else {
        // If the only visible error is the PO-required error, clear it
        if (this.errorEl && !this.errorEl.hidden && this.errorEl.textContent === 'PO number is required.') {
          this.setError(null);
        }
      }

      return ok;
    }

    async savePoNumber() {
      if (!this.poInput) return true;

      const po = (this.poInput.value || '').trim();

      // Avoid extra requests
      if (this._lastSavedPo === po) return true;

      try {
        const res = await fetch(this.getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            attributes: { po_number: po } // empty string clears it
          })
        });

        if (!res.ok) return false;

        this._lastSavedPo = po;
        return true;
      } catch (e) {
        return false;
      }
    }

    // -----------------------
    // Cart sync (discount msg + in-cart counts)
    // -----------------------
    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        return null;
      }
    }

    updateInCartIndicators(cart) {
      // Requires your Liquid to render:
      //  - data-in-cart-line
      //  - data-variant-id
      //  - data-in-cart-count
      const els = this.querySelectorAll('[data-in-cart-line][data-variant-id]');
      if (!els.length) return;

      const qtyByVariant = {};
      (cart?.items || []).forEach((item) => {
        const id = String(item.variant_id);
        qtyByVariant[id] = (qtyByVariant[id] || 0) + (item.quantity || 0);
      });

      els.forEach((el) => {
        const id = String(el.dataset.variantId || '');
        const qty = qtyByVariant[id] || 0;

        const countEl = el.querySelector('[data-in-cart-count]');
        if (countEl) countEl.textContent = String(qty);

        el.hidden = qty <= 0;
      });
    }

        formatMoney(cents) {
      const value = typeof cents === 'number' ? cents : parseInt(cents, 10);
      if (Number.isFinite(value)) {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const fmt = (window.theme && theme.moneyFormat) || (window.Shopify && Shopify.money_format) || '${{amount}}';
          try {
            return Shopify.formatMoney(value, fmt);
          } catch (e) {
            // fall through
          }
        }
        const dollars = value / 100;
        return `$${dollars.toFixed(2)}`;
      }
      return '$0.00';
    }

    buildFreeShippingMessage(totalPriceCents) {
      const thresholdCents = 500 * 100; // $500.00
      const total = typeof totalPriceCents === 'number' ? totalPriceCents : parseInt(totalPriceCents, 10);

      if (!Number.isFinite(total)) return '';

      if (total >= thresholdCents) {
        return `<p>You've unlocked <strong>free shipping</strong> (orders ${this.formatMoney(thresholdCents)}+).</p>`;
      }

      const remaining = thresholdCents - total;
      return `<p>Add <strong>${this.formatMoney(remaining)}</strong> more to unlock <strong>free shipping</strong>`;
    }

    buildDiscountMessage(cart) {
      const itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

      // Keep placeholder until cart has qty > 0
      if (!itemCount || itemCount <= 0) return null;

      const plural = (n) => (n === 1 ? '' : 's');

      let discountMsg = '';
      if (itemCount >= 1 && itemCount < 8) {
        const need = 8 - itemCount;
        discountMsg = `<p>Add <strong>${need}</strong> more item${plural(need)} to cart to unlock <strong>3% off</strong>.</p>`;
      } else if (itemCount >= 8 && itemCount < 16) {
        const need = 16 - itemCount;
        discountMsg = `<p>You've unlocked <strong>3% off</strong>. Add <strong>${need}</strong> more item${plural(need)} to cart to unlock <strong>5% off</strong>.</p>`;
      } else if (itemCount >= 16 && itemCount < 24) {
        const need = 24 - itemCount;
        discountMsg = `<p>You've unlocked <strong>5% off</strong>. Add <strong>${need}</strong> more item${plural(need)} to cart to unlock <strong>7% off</strong>.</p>`;
      } else {
        discountMsg = `<p>You've unlocked <strong>7% off</strong> (24+ items).</p>`;
      }

      const freeShipMsg = this.buildFreeShippingMessage(cart.total_price);
      return `${discountMsg}${freeShipMsg}`;
    }

    updateStickyBarMessage(cart) {
      // Sticky bar message may be outside; ensure we have the reference
      this.bindExternalControls();

      if (!this.barMsgEl) return;

      const html = this.buildDiscountMessage(cart);
      if (html) {
        this.barMsgEl.innerHTML = html;
      } else {
        this.barMsgEl.innerHTML = this.placeholderHtml || this.barMsgEl.innerHTML;
      }
    }

    async syncFromCart() {
      const cart = await this.fetchCart();
      if (!cart) return;

      this.updateStickyBarMessage(cart);
      this.updateInCartIndicators(cart);
    }

    // -----------------------
    // Add to cart
    // -----------------------
    async addSelectedToCart() {
      this.setError(null);
      this.setStatus(null);

      // In case sticky bar was rendered after initial bind
      this.bindExternalControls();

      const items = this.getSelectedItems();
      if (!items.length) {
        this.setError('Add a quantity for at least one item.');
        return;
      }

      // PO number may be required depending on customer tag
      if (!this.validatePoRequired()) return;

      // Save PO before adding items (so the cart always carries it)
      const poOk = await this.savePoNumber();
      if (!poOk) {
        this.setError('Could not save PO number. Please try again.');
        return;
      }

      if (!this.addBtn) return;

      this.addBtn.setAttribute('aria-disabled', 'true');
      this.addBtn.classList.add('is-loading');
      this.addBtn.setAttribute('disabled', 'disabled');

      const formData = new FormData();

      const sections = this.cartDrawer
        ? `${this.cartDrawer.dataset.section},cart-icon-bubble`
        : 'cart-icon-bubble';

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      items.forEach((item, idx) => {
        formData.append(`items[${idx}][id]`, item.id);
        formData.append(`items[${idx}][quantity]`, String(item.quantity));
      });

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) {
          const error = typeof data.description === 'string'
            ? data.description
            : (data.message || 'Unable to add items.');
          throw new Error(error);
        }

        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (this.cartDrawer && data.sections) {
          await this.cartDrawer.renderContents(data, this.openDrawer);
          if (this.openDrawer) {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
          }
        } else if (this.openDrawer) {
          window.location.href = theme.routes.cart;
        }

        const totalUnits = items.reduce((sum, i) => sum + i.quantity, 0);
        this.setStatus(`Added ${totalUnits} item${totalUnits === 1 ? '' : 's'} to cart.`);

        this.resetQuantities();

        // Update discount message + in-cart counts immediately
        await this.syncFromCart();
      } catch (err) {
        this.setError(err.message || 'Unable to add items.');
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.addBtn.removeAttribute('disabled');
        this.addBtn.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('wholesale-picklist', WholesalePicklist);
}